import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';
import { getCategoryBySlug } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryData = getCategoryBySlug(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  const filteredComics = useMemo(() => {
    let result = comics.filter(c => c.category === slug);

    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }

    switch (sortBy) {
      case 'popular':
        return [...result].sort((a, b) => b.views - a.views);
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      case 'latest':
      default:
        return [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
  }, [slug, sortBy, statusFilter]);

  if (!categoryData) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-900">分类不存在</h2>
        </div>
      </div>
    );
  }

  const catData = categories.find(c => c.slug === slug);

  const schemaJson = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: categoryData.seoTitle,
      description: categoryData.seoDescription,
      url: `https://www.manhuawang.cn/category/${slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '韩国漫画网',
        url: 'https://www.manhuawang.cn',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.manhuawang.cn' },
        { '@type': 'ListItem', position: 2, name: `${categoryData.name}韩漫`, item: `https://www.manhuawang.cn/category/${slug}` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={categoryData.seoTitle}
        description={categoryData.seoDescription}
        keywords={categoryData.seoKeywords}
        canonicalUrl={`https://www.manhuawang.cn/category/${slug}`}
        ogTitle={`${categoryData.name}韩漫 - 韩国漫画网`}
        ogDescription={categoryData.seoDescription}
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${categoryData.name}韩漫` },
              ]}
            />

            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                <i className={`${categoryData.icon} text-2xl md:text-3xl`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  {categoryData.name}韩漫
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{categoryData.description}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8 pb-6 border-b border-background-200/70">
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full px-1 py-1">
                {(['latest', 'popular', 'rating'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => { setSortBy(s); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === s
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {s === 'latest' ? '最新更新' : s === 'popular' ? '最受欢迎' : '评分最高'}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1.5 bg-background-100 rounded-full px-1 py-1">
                {(['all', '连载中', '已完结'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => { setStatusFilter(s); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      statusFilter === s
                        ? 'bg-secondary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {s === 'all' ? '全部' : s}
                  </button>
                ))}
              </div>

              <span className="text-xs text-foreground-400 ml-auto">
                共 {filteredComics.length} 部作品
              </span>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 pb-14">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    slug={comic.slug}
                    title={comic.title}
                    author={comic.author}
                    coverImage={comic.coverImage}
                    description={comic.description}
                    category={comic.category}
                    categoryName={catData?.name || comic.category}
                    status={comic.status}
                    rating={comic.rating}
                    views={comic.views}
                    updatedAt={comic.updatedAt}
                    chapterCount={comic.chapterCount}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-search-line text-3xl"></i>
                </div>
                <p className="text-foreground-500">暂无符合条件的漫画</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}